import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { isValidTelephoneNumber } from "app/shared/helpers/helpers";
import { SearchItem } from "app/shared/models/search-param.interface";
import { NGXLogger } from "ngx-logger";
import { Event as RouterEvent, NavigationEnd, Router } from "@angular/router";
/**
 * The service checks search response and prevents sending next requests if response is empty
 */
@Injectable({
  providedIn: "root",
})
export class SearchService {
  private isSearchResponseEmpty: boolean = false;
  private previousSearchString: string;

  private isSearchClearedSource: Subject<void> = new Subject<void>();
  private searchItemSource: Subject<SearchItem> = new Subject<SearchItem>();
  private submittedSearchSource: Subject<string | void> = new Subject<
    string | void
  >();
  private validPhoneNumberSearchStringSource: Subject<string> =
    new Subject<string>();

  private canShowSearchSource: BehaviorSubject<string> =
    new BehaviorSubject<string>(this.router.url);

  public searchItem: SearchItem = { searchString: "" };

  public isSearchCleared$: Observable<void> =
    this.isSearchClearedSource.asObservable();
  public canShowSearchInput$: Observable<string> =
    this.canShowSearchSource.asObservable();
  public searchItem$: Observable<SearchItem> =
    this.searchItemSource.asObservable();
  public validPhoneNumberSearchString$: Observable<string> =
    this.validPhoneNumberSearchStringSource.asObservable();

  public submittedSearch$: Observable<string | void> =
    this.submittedSearchSource.asObservable();

  constructor(
    private logger: NGXLogger,
    private router: Router,
  ) {
    this.subscribeToRouterChange();
  }

  public emitSubmittedSearch(searchString?: string): void {
    this.submittedSearchSource.next(searchString);
  }

  public isSearchIncluded(pageUrl: string): boolean {
    const excludedPagePath: string = "/admin/nconnect-voice";
    return !pageUrl.includes(excludedPagePath);
  }

  public setSearchItem(searchItem: SearchItem): void {
    this.logger.debug(`Searching for:`, searchItem.searchString);
    this.searchItem = searchItem;
    this.searchItemSource.next(searchItem);
  }

  public searchViaDialpad(
    dialpadChar: string,
    shouldReplaceFullString: boolean = false,
  ): void {
    this.logger.debug(`Searching via dialpad for:`, dialpadChar);
    this.searchItem.searchString = shouldReplaceFullString
      ? dialpadChar
      : `${this.searchItem.searchString}${dialpadChar}`;
    this.setSearchItem(this.searchItem);
  }

  public isSearchable(): boolean {
    if (this.hasPreviousResult() || this.hasNewCharAddedInBetween()) {
      return true;
    } else if (isValidTelephoneNumber(this.searchItem.searchString)) {
      this.validPhoneNumberSearchStringSource.next(
        this.searchItem.searchString,
      );
    }
    return false;
  }

  public setSearchResponseEmpty(isResponseEmpty: boolean): void {
    this.isSearchResponseEmpty = isResponseEmpty;
    this.previousSearchString = this.searchItem.searchString;
  }

  public setSearchAvailability(pageUrl: string): void {
    this.canShowSearchSource.next(pageUrl);
  }

  public clearSearch(): void {
    this.setSearchItem({ searchString: "" });
    this.setSearchResponseEmpty(false);
    this.isSearchClearedSource.next();
    this.logger.info(`Search response is empty.`);
  }

  /*
   Method to check whether search input should be shown or not based on page url
   */
  public checkSearchAvailability(pageUrl: string): void {
    const canShowSearch: boolean = this.isSearchIncluded(pageUrl);
    this.setSearchAvailability(canShowSearch ? pageUrl : "");
  }

  private hasPreviousResult(): boolean {
    const hasPreviousSearchResult: boolean = !(
      this.isSearchResponseEmpty &&
      this.previousSearchString.length < this.searchItem.searchString.length
    ); // check old searchkey has result, if not dont call API for current searchkey
    return hasPreviousSearchResult;
  }

  private hasNewCharAddedInBetween(): boolean {
    const hasNewCharAddedInBetween: boolean =
      this.previousSearchString !==
      this.searchItem.searchString.slice(0, this.previousSearchString.length); // check new search string has a new character in between strings. if so, call API
    return hasNewCharAddedInBetween;
  }

  private subscribeToRouterChange(): void {
    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) {
        this.checkSearchAvailability(event.url);
      }
    });
  }
}
